import React, { useState } from 'react';
import { getFeatureDoc } from './feature-docs';

interface FeatureDocumentationProps {
  featureName: string;
  featureKey: string;
}

const FeatureDocumentation: React.FC<FeatureDocumentationProps> = ({ featureName, featureKey }) => {
  const doc = getFeatureDoc(featureKey);
  
  // State for expand/collapse sections
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    overview: true, // Expanded by default
    architecture: false,
    authentication: false,
    authorization: false,
    endpoints: false,
    inputOutput: false,
    security: false,
    mobileAppImplication: false,
    dataFlow: false,
    dataModel: false,
    furtherBrainstorming: false,
  });

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  if (!doc) {
    return (
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <p className="text-gray-600 dark:text-gray-400">Documentation for {featureName} coming soon...</p>
      </div>
    );
  }
  return (
    <div className="mb-6 space-y-6">
      {/* Overview Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{doc.title}</h2>
        </div>
        <p className="text-gray-700 dark:text-gray-300 mb-6">{doc.description}</p>

        {doc.overview && (
          <div className="space-y-4">
            {doc.overview.purpose && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Purpose</h3>
                <p className="text-gray-600 dark:text-gray-400">{doc.overview.purpose}</p>
              </div>
            )}

            {(doc.overview.useCases || doc.overview.keyFeatures) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {doc.overview.useCases && doc.overview.useCases.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Use Cases</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                      {doc.overview.useCases.map((useCase, idx) => (
                        <li key={idx}>{useCase}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {doc.overview.keyFeatures && doc.overview.keyFeatures.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Key Features</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                      {doc.overview.keyFeatures.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Architecture Section */}
      {doc.architecture && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('architecture')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Architecture</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.architecture ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.architecture && (
            <div>

          {doc.architecture.components && doc.architecture.components.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Components</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {doc.architecture.components.map((component, idx) => (
                  <div key={idx} className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{component.name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{component.description}</p>
                    {component.responsibilities && component.responsibilities.length > 0 && (
                      <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        {component.responsibilities.map((resp, rIdx) => (
                          <li key={rIdx}>{resp}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {doc.architecture.workflows && Object.keys(doc.architecture.workflows).length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Workflows</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(doc.architecture.workflows).map(([workflowName, steps]) => (
                  <div key={workflowName} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <h4 className="text-md font-bold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-300 dark:border-gray-600">
                      {workflowName}
                    </h4>
                    <div className="space-y-3">
                      {steps.map((flow, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                            {flow.step}
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-900 dark:text-white text-sm">{flow.title}</h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{flow.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
            </div>
          )}
        </div>
      )}

      {/* Authentication Section */}
      {doc.authentication && doc.authentication.methods && doc.authentication.methods.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('authentication')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Authentication</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.authentication ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.authentication && (
            <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {doc.authentication.methods.map((authMethod, methodIdx) => (
              <div key={methodIdx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-300 dark:border-gray-600">
                  {authMethod.method}
                </h3>
                {authMethod.description && (
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400">{authMethod.description}</p>
                  </div>
                )}
                {authMethod.flow && (
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Authentication Flow</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{authMethod.flow}</p>
                  </div>
                )}
                {authMethod.credentials && authMethod.credentials.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Credentials</h4>
                    <div className="space-y-2">
                      {authMethod.credentials.map((cred, idx) => (
                        <div key={idx} className="bg-white dark:bg-gray-800 rounded p-2">
                          <h5 className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{cred.type}</h5>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{cred.description}</p>
                          {cred.example && (
                            <code className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded block">{cred.example}</code>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {authMethod.tokenManagement && (
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Token Management</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{authMethod.tokenManagement}</p>
                  </div>
                )}
                {authMethod.sessionHandling && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Session Handling</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{authMethod.sessionHandling}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
            </div>
          )}
        </div>
      )}

      {/* Authorization Section */}
      {doc.authorization && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('authorization')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Authorization</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.authorization ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.authorization && (
            <div>
          {doc.authorization.model && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Model</h3>
              <p className="text-gray-600 dark:text-gray-400">{doc.authorization.model}</p>
            </div>
          )}
          {doc.authorization.description && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Description</h3>
              <p className="text-gray-600 dark:text-gray-400">{doc.authorization.description}</p>
            </div>
          )}
          {doc.authorization.roles && doc.authorization.roles.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Roles</h3>
              <div className="space-y-3">
                {doc.authorization.roles.map((role, idx) => (
                  <div key={idx} className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{role.name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{role.description}</p>
                    {role.permissions && role.permissions.length > 0 && (
                      <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        {role.permissions.map((perm, pIdx) => (
                          <li key={pIdx}>{perm}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {doc.authorization.accessControl && doc.authorization.accessControl.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Access Control</h3>
              <div className="space-y-3">
                {doc.authorization.accessControl.map((ac, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded p-3">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{ac.resource}</h4>
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {ac.rules.map((rule, rIdx) => (
                        <li key={rIdx}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          {doc.authorization.policies && doc.authorization.policies.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Policies</h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                {doc.authorization.policies.map((policy, idx) => (
                  <li key={idx}>{policy}</li>
                ))}
              </ul>
            </div>
          )}
            </div>
          )}
        </div>
      )}

      {/* Endpoints Section */}
      {doc.endpoints && doc.endpoints.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('endpoints')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">API Endpoints</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.endpoints ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.endpoints && (
            <div>
          <div className="space-y-6">
            {doc.endpoints.map((endpoint, idx) => (
              <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-2 mb-3">
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded text-xs font-mono">
                    {endpoint.endpoint.split(' ')[0]}
                  </span>
                  <code className="text-sm font-mono text-gray-900 dark:text-white">
                    {endpoint.endpoint.split(' ').slice(1).join(' ')}
                  </code>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{endpoint.description}</p>
                
                {endpoint.note && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mb-4 italic bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                    {endpoint.note}
                  </p>
                )}

                {(endpoint.inputs || endpoint.outputs) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {endpoint.inputs && endpoint.inputs.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Inputs:</h5>
                        <div className="space-y-2">
                          {endpoint.inputs.map((input, iIdx) => (
                            <div key={iIdx} className="bg-white dark:bg-gray-800 rounded p-2 border border-gray-200 dark:border-gray-600">
                              <div className="flex items-start space-x-2">
                                <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded text-xs font-mono">{input.name}</code>
                                <span className="text-xs text-gray-500 dark:text-gray-400">({input.type})</span>
                                {input.required && <span className="text-xs text-red-600 dark:text-red-400">*</span>}
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{input.description}</p>
                              {input.default && (
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Default: {input.default}</p>
                              )}
                              {input.values && input.values.length > 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Values: {input.values.join(', ')}</p>
                              )}
                              {input.constraints && input.constraints.length > 0 && (
                                <ul className="text-xs text-gray-500 dark:text-gray-500 mt-1 list-disc list-inside">
                                  {input.constraints.map((constraint, cIdx) => (
                                    <li key={cIdx}>{constraint}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {endpoint.outputs && endpoint.outputs.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Outputs:</h5>
                        <div className="space-y-3">
                          {endpoint.outputs.map((output, oIdx) => (
                            <div key={oIdx} className="bg-white dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs font-semibold">
                                  {output.status}
                                </span>
                                <span className="text-xs font-semibold text-gray-900 dark:text-white">{output.scenario}</span>
                              </div>
                              {output.response && (
                                <div className="mt-2">
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Type: {output.response.type}</p>
                                  {output.response.fields && output.response.fields.length > 0 && (
                                    <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 mt-1">
                                      {output.response.fields.map((field, fIdx) => (
                                        <li key={fIdx}>
                                          <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{field.name}</code>
                                          {' '}({field.type}) - {field.description}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {output.response.example && (
                                    <code className="block mt-2 text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                                      {output.response.example}
                                    </code>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
            </div>
          )}
        </div>
      )}

      {/* Input/Output Section */}
      {doc.inputOutput && doc.inputOutput.dataFormats && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('inputOutput')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Data Formats</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.inputOutput ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.inputOutput && (
            <div>

          {/* Data Formats */}
          {doc.inputOutput.dataFormats && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Data Formats</h3>
              
              {doc.inputOutput.dataFormats.input && doc.inputOutput.dataFormats.input.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Input Formats:</h4>
                  <div className="space-y-3">
                    {doc.inputOutput.dataFormats.input.map((format, idx) => (
                      <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-1">{format.format}</h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{format.description}</p>
                        {format.features && format.features.length > 0 && (
                          <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc list-inside">
                            {format.features.map((feature, fIdx) => (
                              <li key={fIdx}>{feature}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {doc.inputOutput.dataFormats.output && doc.inputOutput.dataFormats.output.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Output Formats:</h4>
                  <div className="space-y-3">
                    {doc.inputOutput.dataFormats.output.map((format, idx) => (
                      <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-1">{format.format}</h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{format.description}</p>
                        {format.structure && (
                          <div className="text-xs">
                            <p className="font-semibold text-gray-900 dark:text-white mb-1">Structure:</p>
                            <pre className="bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-x-auto">
                              {JSON.stringify(format.structure, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
            </div>
          )}
        </div>
      )}

      {/* Security Section */}
      {doc.security && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('security')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Security</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.security ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.security && (
            <div>
          <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
            {Object.entries(doc.security).map(([key, value]) => (
              <li key={key}>
                <span className="font-semibold text-gray-900 dark:text-white">{key.replace(/([A-Z])/g, ' $1').trim()}:</span> {value as string}
              </li>
            ))}
          </ul>
            </div>
          )}
        </div>
      )}

      {/* Mobile App Implication */}
      {doc.mobileAppImplication && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('mobileAppImplication')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Mobile App Implication</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.mobileAppImplication ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.mobileAppImplication && (
            <div>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <span className={`px-4 py-2 rounded-full font-semibold text-sm ${
                doc.mobileAppImplication.applicable
                  ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                  : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
              }`}>
                {doc.mobileAppImplication.applicable ? 'Applicable' : 'Not Applicable'}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {doc.mobileAppImplication.description}
            </p>
          </div>
            </div>
          )}
        </div>
      )}

      {/* Data Flow Section */}
      {doc.dataFlow && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('dataFlow')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Data Flow</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.dataFlow ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.dataFlow && (
            <div>
          {doc.dataFlow.inputFormats && doc.dataFlow.inputFormats.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Input Formats</h3>
              <div className="flex flex-wrap gap-2">
                {doc.dataFlow.inputFormats.map((format, idx) => (
                  <span key={idx} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium">
                    {format}
                  </span>
                ))}
              </div>
            </div>
          )}
          {doc.dataFlow.stages && doc.dataFlow.stages.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Storage Stages</h3>
              <div className="space-y-4">
                {doc.dataFlow.stages.map((stage, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-md font-bold text-gray-900 dark:text-white mb-1">{stage.stage}</h4>
                        {stage.formats && stage.formats.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {stage.formats.map((format, fIdx) => (
                              <span key={fIdx} className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs">
                                {format}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        stage.storage.duration === 'persistent'
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : stage.storage.duration === 'temporary'
                          ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                          : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                      }`}>
                        {stage.storage.duration.toUpperCase()}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">Storage Type: </span>
                        <span className="text-xs text-gray-600 dark:text-gray-400">{stage.storage.type}</span>
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">Location: </span>
                        <span className="text-xs text-gray-600 dark:text-gray-400">{stage.storage.location}</span>
                      </div>
                      {stage.storage.path && (
                        <div>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">Path: </span>
                          <code className="text-xs bg-gray-200 dark:bg-gray-600 px-1 py-0.5 rounded text-gray-800 dark:text-gray-200">
                            {stage.storage.path}
                          </code>
                        </div>
                      )}
                      <div>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">Description: </span>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{stage.storage.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
            </div>
          )}
        </div>
      )}

      {/* Data Models Section */}
      {doc.dataModel && doc.dataModel.tables && doc.dataModel.tables.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={() => toggleSection('dataModel')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Data Models</h2>
            <svg
              className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expandedSections.dataModel ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.dataModel && (
            <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {doc.dataModel.tables.map((table, idx) => (
              <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 pb-2 border-b border-gray-300 dark:border-gray-600">
                  {table.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{table.description}</p>
                {table.fields && table.fields.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Fields:</h4>
                    <div className="space-y-1.5 max-h-96 overflow-y-auto">
                      {table.fields.map((field, fIdx) => (
                        <div key={fIdx} className="bg-white dark:bg-gray-800 rounded p-2 border border-gray-200 dark:border-gray-600">
                          <div className="flex items-start space-x-2 mb-1">
                            <code className="text-xs font-mono font-semibold text-blue-600 dark:text-blue-400">{field.name}</code>
                            <span className="text-xs text-gray-500 dark:text-gray-400">({field.type})</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{field.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
            </div>
          )}
        </div>
      )}

      {/* Further Brainstorming Section */}
      {doc.furtherBrainstorming && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg shadow-sm border-2 border-yellow-300 dark:border-yellow-700 p-6">
          <button
            onClick={() => toggleSection('furtherBrainstorming')}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h2 className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">Further Brainstorming</h2>
            <svg
              className={`w-6 h-6 text-yellow-700 dark:text-yellow-300 transition-transform duration-200 ${
                expandedSections.furtherBrainstorming ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.furtherBrainstorming && (
            <div>
          
          {doc.furtherBrainstorming.questions && doc.furtherBrainstorming.questions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-3 flex items-center">
                <span className="mr-2">❓</span>
                Questions
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                {doc.furtherBrainstorming.questions.map((question, idx) => (
                  <li key={idx} className="pl-2">{question}</li>
                ))}
              </ul>
            </div>
          )}

          {doc.furtherBrainstorming.blockingIssues && doc.furtherBrainstorming.blockingIssues.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-3 flex items-center">
                <span className="mr-2">🚫</span>
                Blocking Issues
              </h3>
              <div className="space-y-3">
                {doc.furtherBrainstorming.blockingIssues.map((blocking, idx) => {
                  // Handle both string and object formats
                  if (typeof blocking === 'string') {
                    return (
                      <div key={idx} className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 pl-4 py-2 rounded">
                        <p className="text-gray-900 dark:text-white">{blocking}</p>
                      </div>
                    );
                  }
                  // Object format with status and description
                  return (
                    <div key={idx} className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 pl-4 py-2 rounded">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{blocking.issue}</h4>
                        {blocking.status && (
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            blocking.status === 'resolved' 
                              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                              : blocking.status === 'in-progress'
                              ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                              : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          }`}>
                            {blocking.status.toUpperCase()}
                          </span>
                        )}
                      </div>
                      {blocking.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{blocking.description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {doc.furtherBrainstorming.notes && doc.furtherBrainstorming.notes.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-3 flex items-center">
                <span className="mr-2">📝</span>
                Notes
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                {doc.furtherBrainstorming.notes.map((note, idx) => (
                  <li key={idx} className="pl-2">{note}</li>
                ))}
              </ul>
            </div>
          )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeatureDocumentation;
